// Automatic Support Workflow — Seite (CODA-2460 / CODA-2746 / CODA-2760)
//
// Tabs:
//   Supports        — TANSS-Supports gefiltert nach clearanceStatus (extern gesteuert)
//   Freigabe-Info   — Hinweis auf externe Freigabe (kein internes Definitions-Management mehr)

import { useState, useCallback, useRef, useLayoutEffect, useEffect, useMemo, createContext, useContext } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import { ChevronDown, ChevronRight, ChevronsDown, ChevronsUp, Search, RefreshCcw, CheckCircle2, Clock, FileText, Send, AlertTriangle, Mail, MailCheck, MailX, Settings, Info, Plus, Pencil, Trash2, Bot, BotOff, Eye, FlaskConical, FilePen, ExternalLink, Loader2 } from 'lucide-react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/lib/api.ts'
import {
  Button,
  Card,
  EmptyState,
  ErrorState,
  LoadingState,
  PageHeader,
  StatusBadge,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  ConfirmDialog,
  Input,
  Switch,
  Modal,
  Textarea,
  useToast,
} from '@/components/ui'
import {
  useAswEmailTemplates,
  type AswEmailTemplate,
  type SapDocumentMode,
} from './AswSettingsSection.tsx'
import { InvoiceTemplatesTab } from './InvoiceTemplatesTab.tsx'

// ─── Types ────────────────────────────────────────────────────────────────────

interface Support {
  id: number | string
  mandantId: number | string
  customerId: number | string
  linkTypeId: number | string
  linkId: number | string
  ticketId?: number | string
  ticketClosed?: boolean
  supportDate?: number
  clearanceStatus?: string
  matchedDefinitionIds?: string[]
  calculatedPrice?: number
  raw: Record<string, unknown>
}

type AiMode = 'always' | 'never' | 'customer_privacy_default_yes' | 'customer_privacy_default_no'

interface AiInvoiceVars {
  textProblem: string
  textWorkDone: string
  textToDo: string
}

interface AiGenerationResult {
  aiUsed: boolean
  aiMode?: AiMode
  invoicePositionId?: string
  reason?: 'ai_disabled' | 'customer_privacy_not_allowed' | 'ai_error'
  error?: string
  generatedAiVariables?: AiInvoiceVars
}

interface InvoicePositionText {
  templateType: 'ticket' | 'service' | 'ware'
  rawText: string
  sapText: string
  pdfText: string
  pdfHtml?: string
  variables?: Record<string, unknown> & { aiResult?: AiGenerationResult }
}

interface MaterialItem {
  supportId: string | number
  rank: number
  quantity: number
  label?: string
  articleNumber?: string
  serialNumber?: string
  charged?: 'CHARGED' | 'NOT_CHARGED' | string
  price?: number
  voucherNumber?: string
  mentionStock?: string
  raw: unknown
}

interface BillingEligibility {
  canBeBilled: boolean
  blockingReasons: string[]
  totalSupportsForTicket: number
  unbilledSupportIds: Array<string | number>
}

interface InvoicePosition {
  id: string
  source: 'TANSS_SUPPORTS' | 'SAP_DELIVERY_NOTES' | 'OUTLOOK_APPOINTMENTS'
  type: 'ticket' | 'service' | 'ware'
  mandantId: string | number
  mandantName?: string
  customerId: string | number
  customerName?: string
  linkTypeId?: string | number
  linkId?: string | number
  ticketTitle?: string
  supportIds: Array<string | number>
  supports: Support[]
  matchedDefinitionIds: Array<string | number>
  clearanceStatusGroup: 'cleared' | 'other'
  materialItem?: MaterialItem
  invoiceText?: InvoicePositionText
  billingEligibility?: BillingEligibility
  calculatedPrice?: number
  raw?: unknown
}

interface CustomerNode {
  customerId: string
  customerName: string
  /** Per-customer AI flag from business_partners.asw_ai_enabled. null = use tenant default. */
  aswAiEnabled?: boolean | null
  invoicePositions: InvoicePosition[]
}

interface MandantNode {
  mandantId: string
  mandantName: string
  customers: CustomerNode[]
}

interface SupportTreeFilterStats {
  loadedFromTanss: number
  skippedUnmappedCompany: number
  skippedContractBound: number
  skippedAlreadyBooked: number
  skippedErpNumberPresent: number
  skippedPendingOrFlaggedReview: number
  clearedCount: number
  otherCount: number
}

interface SupportTreeResponse {
  meta: { daysBack: number; clearedCount: number; otherCount: number; fetchedAt?: string | null; timings?: Record<string, number>; filterStats?: SupportTreeFilterStats }
  cleared: MandantNode[]
  other: MandantNode[]
}


interface InvoiceEmailResult {
  mandantId: string
  customerId: string
  sapDocumentMode: string
  invoiceNumber: string
  invoiceDocEntry: number
  originalRecipientEmail?: string
  actualRecipientEmail?: string
  redirectedBecause?: string
  pdfCreated: boolean
  pdfSource?: string
  attachmentAdded?: boolean
  emailAttempted?: boolean
  emailSent: boolean
  reason?: string
  subject?: string
  attachmentName?: string
  sender?: string
  templateName?: string
  customerEmailSource?: string
  contactId?: string
  contactName?: string
  error?: string
  pdfDebug?: {
    pdfRequested?: boolean
    pdfGenerationStarted?: boolean
    pdfSourceAttempted?: string
    workflowInvoiceDocumentCreated?: boolean
    workflowInvoiceDocumentPreview?: {
      mandantName?: string
      customerName?: string
      cardCode?: string
      invoiceNumber?: string
      invoiceDate?: string
      positionCount?: number
      total?: number
      currency?: string
    }
    documentLinesCount?: number
    pdfRendererCalled?: boolean
    pdfRendererName?: string
    pdfCreated?: boolean
    pdfBufferExists?: boolean
    pdfBufferLength?: number
    htmlLength?: number
    attachmentCreated?: boolean
    attachmentName?: string
    errorStep?: string
    errorMessage?: string
    errorStack?: string
  }
}

interface SkippedPosition {
  positionId: string
  reason: string
}

interface TransferResultItem {
  mandantId: string
  mandantName: string
  customerId: string
  customerName: string
  cardCode: string
  mode: SapDocumentMode
  endpoint: string
  success: boolean
  devMode: boolean
  sapDocEntry?: number
  sapDocNum?: number
  error?: string
  skippedPositions?: SkippedPosition[]
  emailResult?: InvoiceEmailResult
  /** true if email sending after transfer happens separately via POST /send-email-from-log */
  emailSkipped?: boolean
  /** Log ID for asynchronous email sending via /send-email-from-log/:logId */
  logId?: string
}

type ClearanceStatusGroup = 'cleared' | 'other'
type FilterMode = 'all' | 'cleared' | 'other'

interface TransferScope {
  clearanceStatusGroup: ClearanceStatusGroup
  mandantId?: string
  customerId?: string
  customerIds?: string[]
  trigger?: string
  skipEmail?: boolean
}

interface TransferReport {
  results: TransferResultItem[]
  skippedMandants: Array<{ mandantId: string; reason: string }>
  totalSent: number
  totalFailed: number
  devModeActive: boolean
  scope?: TransferScope
}

interface DevSapCall {
  endpoint: string
  method: 'POST'
  payload: unknown
}

interface PlannedTanssAction {
  type: string
  supportIds?: Array<string | number>
  skippedBecause: 'dev_mode'
}

interface AswLog {
  id: string
  mode: 'dev' | 'live'
  createdAt: string
  mandantId: string
  mandantName?: string
  customerId: string
  customerName?: string
  sapDocumentMode?: SapDocumentMode
  invoiceDate?: string
  invoiceDueDate?: string
  total?: number
  currency?: string
  status: 'success' | 'error' | 'skipped'
  summary: { invoicePositionCount?: number; supportIds?: Array<string | number>; message?: string; emailResult?: InvoiceEmailResult }
  devSapCalls?: DevSapCall[]
  plannedTanssActions?: PlannedTanssAction[]
  sapDocEntry?: number
  sapDocNum?: number
  error?: string
}

// ─── Price Formatting ─────────────────────────────────────────────────────────

function fmtEur(amount: number): string {
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount)
}

// ─── Support Exclusion Context (AC14 — individual Leistung deselection) ───────

interface SupportExclusionContextValue {
  excludedSupportIds: Set<string | number>
  onToggleSupportExclusion: (id: string | number) => void
}

const SupportExclusionContext = createContext<SupportExclusionContextValue | null>(null)

function useSupportExclusion(): SupportExclusionContextValue {
  const ctx = useContext(SupportExclusionContext)
  if (!ctx) throw new Error('SupportExclusionContext missing')
  return ctx
}

// ─── API Hooks ────────────────────────────────────────────────────────────────

function useSupportTree() {
  return useQuery<SupportTreeResponse>({
    queryKey: ['automatic-support-workflow'],
    queryFn: async () => {
      const t0 = performance.now()
      console.log('[ASW] GET /preview — Anfrage gestartet')
      const res = await apiClient.get<SupportTreeResponse>('/automatic-support-workflow/preview')
      const elapsed = Math.round(performance.now() - t0)
      const t = res.data.meta.timings
      console.log(`[ASW] GET /preview — fertig in ${elapsed}ms (Browser→Server→Browser)`, {
        clearedCount: res.data.meta.clearedCount,
        otherCount: res.data.meta.otherCount,
        fetchedAt: res.data.meta.fetchedAt,
        serverTimings: t ? `gesamt=${t.totalMs}ms, TANSS-API=${t.tanssApiMs}ms, Rechnungstexte=${t.invoiceTextsMs}ms` : '(aus Cache)',
      })
      return res.data
    },
    staleTime: Infinity,
  })
}

function useRefreshSupportTree() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      const t0 = performance.now()
      console.log('[ASW] POST /preview/refresh — Neu laden gestartet')
      const res = await apiClient.post<SupportTreeResponse>('/automatic-support-workflow/preview/refresh', {})
      const elapsed = Math.round(performance.now() - t0)
      const t = res.data.meta.timings
      console.log(`[ASW] POST /preview/refresh — fertig in ${elapsed}ms`, {
        clearedCount: res.data.meta.clearedCount,
        otherCount: res.data.meta.otherCount,
        serverTimings: t ? `gesamt=${t.totalMs}ms, TANSS-API=${t.tanssApiMs}ms, Rechnungstexte=${t.invoiceTextsMs}ms` : 'n/a',
      })
      return res.data
    },
    onSuccess: (data) => {
      qc.setQueryData(['automatic-support-workflow'], data)
    },
  })
}

function useCreateAswEmailTemplate() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (input: { name: string; subjectTemplate: string; bodyTemplate: string; active?: boolean }) => {
      const res = await apiClient.post<AswEmailTemplate>('/automatic-support-workflow/email-templates', input)
      return res.data
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['asw-email-templates'] }),
  })
}

function useUpdateAswEmailTemplate() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, patch }: { id: string; patch: Partial<Pick<AswEmailTemplate, 'name' | 'subjectTemplate' | 'bodyTemplate' | 'active'>> }) => {
      const res = await apiClient.patch<AswEmailTemplate>(`/automatic-support-workflow/email-templates/${id}`, patch)
      return res.data
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['asw-email-templates'] }),
  })
}

function useDeleteAswEmailTemplate() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/automatic-support-workflow/email-templates/${id}`)
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['asw-email-templates'] }),
  })
}


function useAswLogs() {
  return useQuery<AswLog[]>({
    queryKey: ['asw-logs'],
    queryFn: async () => {
      const res = await apiClient.get<AswLog[]>('/automatic-support-workflow/logs?limit=100')
      return res.data
    },
    staleTime: 30_000,
  })
}

function useTransfer() {
  return useMutation({
    mutationFn: async (scope: TransferScope) => {
      const res = await apiClient.post<TransferReport>('/automatic-support-workflow/transfer', { ...scope, skipEmail: true })
      return res.data
    },
  })
}

interface SendEmailParams {
  mandantId: string
  mandantName: string
  customerId: string
  customerName: string
  cardCode: string
  mode: SapDocumentMode
  devMode: boolean
  sapDocEntry?: number
  sapDocNum?: number
  positions: Array<{
    id: string
    type: 'ticket' | 'service' | 'ware'
    linkId?: string | number | null
    ticketTitle?: string | null
    supportIds: Array<string | number>
    invoiceText?: { rawText?: string; pdfText?: string; pdfHtml?: string } | null
    materialItem?: { articleNumber?: string; label?: string; quantity?: number; price?: number } | null
    calculatedPrice?: number
  }>
}

function useSendEmail() {
  return useMutation({
    mutationFn: async (params: SendEmailParams) => {
      const res = await apiClient.post<InvoiceEmailResult>('/automatic-support-workflow/send-email', params)
      return res.data
    },
  })
}

// @ts-expect-error -- kept for future use
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function useSendEmailFromLog() {
  return useMutation({
    mutationFn: async (logId: string) => {
      const res = await apiClient.post<InvoiceEmailResult>(`/automatic-support-workflow/send-email-from-log/${logId}`, {})
      return res.data
    },
  })
}

function useEnrichAi() {
  return useMutation({
    mutationFn: async () => {
      const res = await apiClient.post<SupportTreeResponse>('/automatic-support-workflow/enrich-ai', {})
      return res.data
    },
  })
}

function useEnrichSingleCustomer() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (tanssCustomerId: string) => {
      const res = await apiClient.post<SupportTreeResponse>('/automatic-support-workflow/enrich-ai', { tanssCustomerId })
      return res.data
    },
    onSuccess: (data) => {
      qc.setQueryData(['automatic-support-workflow'], data)
    },
  })
}

function useToggleCustomerAi() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ tanssCustomerId, aiEnabled }: { tanssCustomerId: string; aiEnabled: boolean | null }) => {
      await apiClient.patch(`/automatic-support-workflow/customer-ai/${tanssCustomerId}`, { aiEnabled })
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['automatic-support-workflow'] }),
  })
}

interface AswTenantSetting {
  tenantId: string
  tenantName: string
  aiMode: AiMode
  sapDocumentMode?: 'draft' | 'invoice'
}

interface CustomerOptionEntry {
  tenantId: string
  customerId: string
  /** null = use module default */
  sapDraft: boolean | null
  /** true = probe/test (safe default), false = production */
  testSend: boolean
}

function useAswTenantSettings() {
  return useQuery<AswTenantSetting[]>({
    queryKey: ['asw-tenant-settings'],
    queryFn: async () => {
      const res = await apiClient.get<AswTenantSetting[]>('/automatic-support-workflow/tenant-settings')
      return res.data
    },
    staleTime: 300_000,
  })
}

function useCustomerOptions() {
  return useQuery<CustomerOptionEntry[]>({
    queryKey: ['asw-customer-options'],
    queryFn: async () => {
      const res = await apiClient.get<CustomerOptionEntry[]>('/automatic-support-workflow/customer-options')
      return res.data
    },
    staleTime: 60_000,
  })
}

function useUpdateCustomerOption() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({
      tenantId,
      customerId,
      patch,
    }: {
      tenantId: string
      customerId: string
      patch: Partial<Pick<CustomerOptionEntry, 'sapDraft' | 'testSend'>>
    }) => {
      const res = await apiClient.patch<CustomerOptionEntry>(
        `/automatic-support-workflow/customer-options/${tenantId}/${customerId}`,
        patch,
      )
      return res.data
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['asw-customer-options'] }),
  })
}

function useBulkUpdateCustomerOptions() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({
      customers,
      patch,
    }: {
      customers: Array<{ tenantId: string; customerId: string }>
      patch: Partial<Pick<CustomerOptionEntry, 'sapDraft' | 'testSend'>>
    }) => {
      await apiClient.post('/automatic-support-workflow/customer-options/bulk', { customers, patch })
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['asw-customer-options'] }),
  })
}

// ─── Invoice Preview ──────────────────────────────────────────────────────────

interface PreviewCustomerInfo {
  mandantId: string
  mandantName: string
  customerId: string
  customerName: string
}

// A4 at 96 dpi (CSS pixels)
const A4_WIDTH_PX = 794

// Merges cleared and other mandant trees into one, combining customers by customerId.
function mergeMandants(cleared: MandantNode[], other: MandantNode[]): MandantNode[] {
  const map = new Map<string, MandantNode>()
  for (const m of [...cleared, ...other]) {
    let node = map.get(m.mandantId)
    if (!node) {
      node = { mandantId: m.mandantId, mandantName: m.mandantName, customers: [] }
      map.set(m.mandantId, node)
    }
    for (const c of m.customers) {
      const existing = node.customers.find((x) => x.customerId === c.customerId)
      if (!existing) {
        node.customers.push({ ...c, invoicePositions: [...c.invoicePositions] })
      } else {
        existing.invoicePositions = [...existing.invoicePositions, ...c.invoicePositions]
      }
    }
  }
  return [...map.values()]
}

function useInvoicePreview(customer: PreviewCustomerInfo | null) {
  const { data: treeData } = useSupportTree()

  // Merge cleared + other to find the customer's positions across both groups
  const allMandants = useMemo(
    () => mergeMandants(treeData?.cleared ?? [], treeData?.other ?? []),
    [treeData?.cleared, treeData?.other],
  )

  // Preview always uses ONLY cleared positions (Aufgabe 2: nicht freigegebene Leistungen
  // erscheinen nicht in der Rechnungsvorschau)
  const livePositions = useMemo(() => {
    if (!customer) return []
    const positions =
      allMandants
        .find((m) => m.mandantId === customer.mandantId)
        ?.customers.find((c) => c.customerId === customer.customerId)?.invoicePositions ?? []
    return positions.filter((p) => p.clearanceStatusGroup === 'cleared')
  }, [allMandants, customer?.mandantId, customer?.customerId])

  const posFingerprint =
    livePositions.map((p) => `${p.id}:${p.invoiceText?.pdfHtml?.length ?? 0}`).join('|')

  return useQuery<string | null>({
    queryKey: [
      'asw-invoice-preview',
      customer?.mandantId,
      customer?.customerId,
      posFingerprint,
    ],
    queryFn: async () => {
      if (!customer) return null
      const res = await apiClient.post<{ html: string }>('/automatic-support-workflow/invoice-preview', {
        mandantId: customer.mandantId,
        customerId: customer.customerId,
        // Always use cleared group for preview — backend will search both trees for metadata
        clearanceStatusGroup: 'cleared',
        positions: livePositions.length > 0 ? livePositions : undefined,
      })
      return res.data.html
    },
    enabled: !!customer,
    staleTime: 30_000,
  })
}

// Scaled iframe: renders the A4 document at natural 794px width, scales it proportionally
// to the container width. Uses ResizeObserver + allow-same-origin contentDocument height.
function ScaledInvoiceIframe({ html }: { html: string }) {
  const containerRef = useRef<HTMLDivElement>(null)
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const [scale, setScale] = useState(1)
  const [contentHeight, setContentHeight] = useState(1122) // default A4 height in px

  useLayoutEffect(() => {
    const el = containerRef.current
    if (!el) return
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width ?? 0
      if (w > 0) setScale(w / A4_WIDTH_PX)
    })
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  const handleLoad = useCallback(() => {
    try {
      const doc = iframeRef.current?.contentDocument
      if (!doc) return
      // Measure document height after fonts/images settle
      window.setTimeout(() => {
        const h = doc.documentElement.scrollHeight || doc.body?.scrollHeight || 1122
        setContentHeight(h)
      }, 150)
    } catch {
      // cross-origin guard — shouldn't happen with allow-same-origin srcdoc
    }
  }, [])

  const visualHeight = contentHeight * scale

  return (
    <div
      ref={containerRef}
      className="flex-1 relative overflow-x-hidden overflow-y-auto bg-neutral-200 dark:bg-neutral-700"
      style={{ minHeight: 0 }}
    >
      {/* Spacer so the outer container has the correct scroll height */}
      <div style={{ height: visualHeight }} />
      {/* Iframe positioned at top-left, scaled to fit */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: A4_WIDTH_PX,
          height: contentHeight,
          transform: `scale(${scale})`,
          transformOrigin: 'top left',
        }}
      >
        <iframe
          ref={iframeRef}
          srcDoc={html}
          onLoad={handleLoad}
          title="Rechnungsvorschau"
          style={{ width: '100%', height: '100%', border: 0, display: 'block' }}
          sandbox="allow-same-origin"
        />
      </div>
    </div>
  )
}

function InvoicePreviewPanel({ customer }: { customer: PreviewCustomerInfo | null }) {
  const { data: html, isLoading, error, isFetching } = useInvoicePreview(customer)

  return (
    <div className="flex flex-col h-full border border-border rounded-lg bg-background overflow-hidden">
      <div className="flex items-center justify-between px-3 py-2 border-b border-border bg-muted/30 shrink-0">
        <div className="min-w-0">
          <div className="text-sm font-semibold text-foreground truncate">Rechnungsvorschau</div>
          {customer ? (
            <div className="text-xs text-muted-foreground truncate">{customer.customerName}</div>
          ) : (
            <div className="text-xs text-muted-foreground">Kunden auswählen</div>
          )}
        </div>
        {isFetching && (
          <div className="ml-2 flex items-center gap-1 text-xs text-muted-foreground shrink-0">
            <RefreshCcw className="h-3 w-3 animate-spin" />
            <span>Lädt…</span>
          </div>
        )}
      </div>

      {!customer && (
        <div className="flex flex-col items-center justify-center flex-1 text-sm text-muted-foreground gap-2 p-6 text-center">
          <Eye className="h-8 w-8 opacity-30" />
          <span>Klicken Sie auf „Vorschau" bei einem Kunden, um die Rechnungsvorschau anzuzeigen.</span>
        </div>
      )}

      {customer && isLoading && !html && (
        <div className="flex items-center justify-center flex-1 text-sm text-muted-foreground">
          <RefreshCcw className="h-4 w-4 animate-spin mr-2" />
          Vorschau wird geladen…
        </div>
      )}

      {customer && error && (
        <div className="p-3 text-sm text-destructive">
          Vorschau konnte nicht geladen werden: {(error as Error).message}
        </div>
      )}

      {customer && html && <ScaledInvoiceIframe html={html} />}

      {customer && !isLoading && !error && !html && (
        <div className="flex items-center justify-center flex-1 text-sm text-muted-foreground p-4 text-center">
          Keine Vorschau verfügbar (noch keine Rechnungstexte generiert)
        </div>
      )}
    </div>
  )
}


// ─── Tree Components ──────────────────────────────────────────────────────────

function FilterDiagPanel({ stats, open, onToggle }: { stats?: SupportTreeFilterStats; open: boolean; onToggle: () => void }) {
  if (!stats) return null
  const totalSkipped = stats.skippedUnmappedCompany + stats.skippedContractBound + stats.skippedAlreadyBooked + stats.skippedErpNumberPresent + stats.skippedPendingOrFlaggedReview
  if (totalSkipped === 0) return null
  return (
    <div className="mb-4 border border-amber-200 bg-amber-50 rounded-lg">
      <button
        className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-amber-800 font-medium hover:bg-amber-100 rounded-lg transition-colors"
        onClick={onToggle}
      >
        <AlertTriangle className="h-4 w-4 text-amber-600 flex-shrink-0" />
        <span>{totalSkipped} Leistung{totalSkipped !== 1 ? 'en' : ''} von {stats.loadedFromTanss} werden ausgeblendet — Filter aktiv</span>
        <ChevronDown className={`h-4 w-4 ml-auto transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="px-4 pb-3 pt-1 text-xs text-amber-900 space-y-1.5 border-t border-amber-200">
          <div className="font-semibold mb-2 text-sm">Warum werden Leistungen nicht angezeigt?</div>
          {stats.skippedPendingOrFlaggedReview > 0 && (
            <div className="flex justify-between gap-4">
              <span>Interne Freigabe fehlt oder beanstandet — bitte in Dispo → Leistungsfreigabe freigeben</span>
              <span className="font-mono font-semibold">{stats.skippedPendingOrFlaggedReview}</span>
            </div>
          )}
          {stats.skippedUnmappedCompany > 0 && (
            <div className="flex justify-between gap-4">
              <span>Firma nicht in Geschäftspartnern verknüpft (TANSS-ID fehlt in Stammdaten)</span>
              <span className="font-mono font-semibold">{stats.skippedUnmappedCompany}</span>
            </div>
          )}
          {stats.skippedContractBound > 0 && (
            <div className="flex justify-between gap-4">
              <span>Vertragsgebundene Leistungen (werden separat über TANSS-Verträge abgerechnet)</span>
              <span className="font-mono font-semibold">{stats.skippedContractBound}</span>
            </div>
          )}
          {stats.skippedAlreadyBooked > 0 && (
            <div className="flex justify-between gap-4">
              <span>Bereits in ERP übertragen (booked=true)</span>
              <span className="font-mono font-semibold">{stats.skippedAlreadyBooked}</span>
            </div>
          )}
          {stats.skippedErpNumberPresent > 0 && (
            <div className="flex justify-between gap-4">
              <span>ERP-Belegnummer bereits vorhanden</span>
              <span className="font-mono font-semibold">{stats.skippedErpNumberPresent}</span>
            </div>
          )}
          <div className="mt-2 pt-2 border-t border-amber-200 text-amber-700 text-xs">
            Gesamt aus TANSS geladen: {stats.loadedFromTanss} · Freigegeben: {stats.clearedCount} · Sonstige: {stats.otherCount} · Ausgeblendet: {totalSkipped}
          </div>
        </div>
      )}
    </div>
  )
}

function ClearanceStatusBadge({ group }: { group: ClearanceStatusGroup }) {
  if (group === 'cleared') {
    return (
      <span className="inline-flex items-center gap-1 text-xs rounded px-1.5 py-0.5 bg-green-100 text-green-700 border border-green-200 shrink-0">
        <CheckCircle2 className="h-3 w-3" />
        Freigegeben
      </span>
    )
  }
  return (
    <span className="inline-flex items-center gap-1 text-xs rounded px-1.5 py-0.5 bg-amber-100 text-amber-700 border border-amber-200 shrink-0">
      <Clock className="h-3 w-3" />
      Nicht freigegeben
    </span>
  )
}

function SupportRow({ support }: { support: Support }) {
  const { excludedSupportIds, onToggleSupportExclusion } = useSupportExclusion()
  const isExcluded = excludedSupportIds.has(support.id)

  const raw = support.raw
  const date = typeof raw.date === 'number'
    ? new Date(raw.date * 1000).toLocaleDateString('de-DE')
    : '–'
  const duration = typeof raw.duration === 'number' ? `${raw.duration} min` : '–'
  const employee = typeof raw.employeeName === 'string' && raw.employeeName
    ? raw.employeeName
    : typeof raw.employeeId === 'number' ? `Mitarbeiter #${raw.employeeId}` : '–'
  const text = typeof raw.text === 'string' ? raw.text : typeof raw.title === 'string' ? raw.title : `Support #${support.id}`

  return (
    <div className={`ml-6 border-l border-border pl-4 py-1.5 text-sm ${isExcluded ? 'opacity-50' : ''}`}>
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1 flex items-start gap-2">
          <input
            type="checkbox"
            checked={!isExcluded}
            onChange={() => onToggleSupportExclusion(support.id)}
            className="h-3.5 w-3.5 mt-0.5 rounded border-border accent-brand shrink-0 cursor-pointer"
            title={isExcluded ? 'Leistung wieder einbeziehen' : 'Leistung aus Rechnung ausschließen'}
          />
          <div className="min-w-0">
            <div className={`flex items-baseline gap-1.5 ${isExcluded ? 'line-through text-muted-foreground' : ''}`}>
              <span className="text-xs text-muted-foreground shrink-0 font-mono">#{support.id}</span>
              <span className="font-medium text-foreground">{text}</span>
            </div>
            <div className="text-muted-foreground text-xs mt-0.5">
              {date} · {duration} · {employee}
            </div>
          </div>
        </div>
        {support.calculatedPrice !== undefined && (
          <span className={`shrink-0 text-xs font-mono tabular-nums ${isExcluded ? 'text-muted-foreground line-through' : 'text-foreground'}`}>
            {fmtEur(support.calculatedPrice)}
          </span>
        )}
      </div>
    </div>
  )
}

function AiResultBadge({ aiResult }: { aiResult: AiGenerationResult }) {
  const [errorOpen, setErrorOpen] = useState(false)

  if (aiResult.aiUsed) {
    return (
      <span className="inline-flex items-center gap-1 text-xs rounded px-1.5 py-0.5 bg-green-100 text-green-700 border border-green-200">
        <Bot className="h-3 w-3" />
        KI-Text generiert
      </span>
    )
  }
  if (aiResult.reason === 'ai_error') {
    const isConfigError = aiResult.error?.includes('API-Key') || aiResult.error?.includes('konfiguriert')
    return (
      <span className="inline-flex flex-col gap-0.5">
        <button
          onClick={() => setErrorOpen((v) => !v)}
          className="inline-flex items-center gap-1 text-xs rounded px-1.5 py-0.5 bg-red-50 text-red-700 border border-red-200 hover:bg-red-100 transition-colors"
        >
          <BotOff className="h-3 w-3" />
          KI-Fehler
          {errorOpen ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
        </button>
        {errorOpen && (
          <span className="text-xs text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1.5 max-w-xs">
            {aiResult.error ?? 'Unbekannter Fehler'}
            {isConfigError && (
              <RouterLink
                to="/einstellungen/adapter"
                className="mt-1 flex items-center gap-1 text-brand underline hover:no-underline"
              >
                → Einstellungen &gt; Adapter &amp; Postfächer
              </RouterLink>
            )}
          </span>
        )}
      </span>
    )
  }
  if (aiResult.reason === 'customer_privacy_not_allowed') {
    return (
      <span className="inline-flex items-center gap-1 text-xs rounded px-1.5 py-0.5 bg-amber-50 text-amber-700 border border-amber-200">
        <BotOff className="h-3 w-3" />
        KI: Datenschutz-Ausschluss
      </span>
    )
  }
  return null
}

// Fixed section headings for AI-generated invoice texts (never from AI — hardcoded per CODA-3214)
const AI_SECTION_HEADINGS = [
  { key: 'textProblem' as const, heading: 'Problemstellung und Lösungsansatz' },
  { key: 'textWorkDone' as const, heading: 'Durchgeführte Arbeiten und Maßnahmen' },
  { key: 'textToDo' as const, heading: 'Noch ausstehende Aufgaben und nächste Schritte' },
]

function InvoiceTextBlock({ invoiceText }: { invoiceText: InvoicePositionText }) {
  const aiResult = invoiceText.variables?.aiResult
  const aiUsed = aiResult?.aiUsed === true
  const generatedAiVars = aiResult?.generatedAiVariables
  // Auto-expand when AI text is present (CODA-3215); collapsed for non-AI positions
  const [open, setOpen] = useState(() => aiUsed && !!generatedAiVars)

  // Sync open state when AI vars arrive after initial mount (e.g. enrichment after page load)
  useEffect(() => {
    if (aiUsed && generatedAiVars) setOpen(true)
  }, [aiUsed, generatedAiVars])
  const typeLabel =
    invoiceText.templateType === 'ticket' ? 'Rechnungstext (Ticket)' :
    invoiceText.templateType === 'service' ? 'Rechnungstext (Leistung)' :
    'Rechnungstext (Ware)'
  return (
    <div className="mt-1.5 ml-1">
      <div className="flex items-center gap-2 flex-wrap">
        <button
          onClick={() => setOpen((v) => !v)}
          className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-brand transition-colors py-0.5"
        >
          <FileText className="h-3 w-3" />
          {typeLabel}
          {open ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
        </button>
        {aiResult && <AiResultBadge aiResult={aiResult} />}
      </div>
      {open && (
        <div className="mt-1 text-xs bg-muted rounded px-3 py-2 border border-border space-y-2.5">
          {aiUsed && generatedAiVars ? (
            // Three-section display using fixed headings + persisted AI vars (CODA-3214)
            AI_SECTION_HEADINGS.map(({ key, heading }) => {
              const content = generatedAiVars[key]
              if (!content?.trim()) return null
              return (
                <div key={key} data-ai-section={key}>
                  <div className="font-semibold text-foreground mb-0.5">{heading}</div>
                  <div className="whitespace-pre-wrap leading-relaxed text-foreground/80">{content}</div>
                </div>
              )
            })
          ) : (
            // Fallback: raw template text for non-AI positions
            <div
              className="whitespace-pre-wrap font-mono leading-relaxed"
              dangerouslySetInnerHTML={{ __html: invoiceText.rawText }}
            />
          )}
        </div>
      )}
    </div>
  )
}

function InvoicePositionGroup({ position }: { position: InvoicePosition }) {
  const [open, setOpen] = useState(true)
  const { excludedSupportIds } = useSupportExclusion()

  const count = position.supports.length
  const ticketNum = position.type === 'ticket' ? String(position.linkId).padStart(6, '0') : null
  const label = ticketNum !== null
    ? position.ticketTitle
      ? `Ticket #${ticketNum} // ${position.ticketTitle}`
      : `Ticket #${ticketNum}`
    : position.type === 'service'
      ? `Leistung #${String(position.supportIds[0] ?? '')}`
      : `Ware: ${position.materialItem?.label ?? position.materialItem?.articleNumber ?? `Support #${String(position.supportIds[0] ?? '')}`}`
  const tanssUrl = ticketNum !== null
    ? `https://tanss.implec.de/index.php?section=bug&sub=view&bugID=${String(position.linkId)}`
    : null

  // Ticket subtotal: dynamic based on selected services (AC11/AC14)
  const hasAnyExclusion = position.supports.some((s) => excludedSupportIds.has(s.id))
  const localTicketTotal = position.supports.reduce((sum, s) => {
    if (excludedSupportIds.has(s.id)) return sum
    return sum + (s.calculatedPrice ?? 0)
  }, 0)
  const displayTicketPrice = hasAnyExclusion ? localTicketTotal : (position.calculatedPrice ?? 0)

  return (
    <div className="ml-4 border-l border-border pl-4 mt-1">
      <div className="flex items-center text-sm font-medium text-foreground py-1 w-full">
        <button
          onClick={() => setOpen((v: boolean) => !v)}
          className="shrink-0 mr-1.5 hover:text-brand transition-colors"
        >
          {open ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
        </button>
        <div className="flex items-center gap-1.5 min-w-0 flex-1">
          <span
            className="truncate cursor-pointer hover:text-brand transition-colors"
            onClick={() => setOpen((v: boolean) => !v)}
          >
            {label}
          </span>

          <ClearanceStatusBadge group={position.clearanceStatusGroup} />

          {ticketNum !== null && (
            <StatusBadge
              status={position.supports[0]?.ticketClosed ? 'closed' : 'open'}
              className="shrink-0"
            />
          )}

          {tanssUrl && (
            <a
              href={tanssUrl}
              target="_blank"
              rel="noopener noreferrer"
              title="Ticket in TANSS öffnen"
              className="shrink-0 text-muted-foreground hover:text-brand transition-colors"
            >
              <ExternalLink className="h-4 w-4" />
            </a>
          )}
        </div>
        <span className="text-xs text-muted-foreground font-normal shrink-0 ml-1.5">({count})</span>
        {position.clearanceStatusGroup === 'cleared' && (position.calculatedPrice !== undefined || position.supports.some((s) => s.calculatedPrice !== undefined)) && (
          <span className="ml-1 text-xs font-mono tabular-nums shrink-0 text-foreground">
            {fmtEur(displayTicketPrice)}
          </span>
        )}
      </div>
      {position.clearanceStatusGroup === 'other' && (
        <div className="ml-2 mt-0.5 mb-1 flex items-start gap-1.5 rounded border border-amber-200 bg-amber-50 px-2 py-1 text-xs text-amber-700">
          <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
          <span>Nicht in Rechnung berücksichtigt — Freigabe ausstehend</span>
        </div>
      )}
      {position.billingEligibility?.canBeBilled === false && position.clearanceStatusGroup === 'cleared' && (
        <div className="ml-2 mt-1 mb-2 flex items-start gap-1.5 rounded border border-amber-300 bg-amber-50 px-2 py-1.5 text-xs text-amber-800">
          <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
          <span>
            <strong>SAP-Übertragung gesperrt:</strong>{' '}
            {position.billingEligibility.blockingReasons.join(' · ')}{' '}
            (Leistungen noch nicht freigegeben:{' '}
            {position.billingEligibility.unbilledSupportIds.map(String).join(', ')})
          </span>
        </div>
      )}
      {open && (
        <>
          {position.invoiceText && (
            <div className="ml-2 mb-2">
              <InvoiceTextBlock invoiceText={position.invoiceText} />
            </div>
          )}
          {position.supports.map((s) => (
            <SupportRow key={String(s.id)} support={s} />
          ))}
        </>
      )}
    </div>
  )
}

function AiCustomerToggle({ customer }: { customer: CustomerNode }) {
  const toggleMut = useToggleCustomerAi()
  const enrichMut = useEnrichSingleCustomer()
  const { toast } = useToast()
  const { aswAiEnabled } = customer
  const isPending = toggleMut.isPending || enrichMut.isPending

  const handleClick = async () => {
    if (aswAiEnabled === false) {
      // Re-enable: cycle back to default (no generation)
      toggleMut.mutate({ tanssCustomerId: customer.customerId, aiEnabled: null })
      return
    }
    // Enabled or default: set true (if not already) then generate AI texts for this customer
    try {
      if (aswAiEnabled !== true) {
        await apiClient.patch(`/automatic-support-workflow/customer-ai/${customer.customerId}`, { aiEnabled: true })
      }
      enrichMut.mutate(customer.customerId, {
        onSuccess: () => toast({ title: 'KI-Texte generiert', tone: 'success' }),
        onError: (err) => toast({ title: 'KI-Generierung fehlgeschlagen', description: (err as Error).message, tone: 'danger' }),
      })
    } catch (err) {
      toast({ title: 'Fehler beim Aktivieren', description: (err as Error).message, tone: 'danger' })
    }
  }

  const label = aswAiEnabled === false
    ? 'KI deaktiviert — klicken zum Reaktivieren'
    : 'KI-Texte für diesen Kunden generieren'

  const chipClass = aswAiEnabled === false
    ? 'text-red-700 bg-red-50 border border-red-200'
    : aswAiEnabled === true
      ? 'text-green-700 bg-green-50 border border-green-200 hover:bg-green-100'
      : 'text-blue-700 bg-blue-50 border border-blue-200 hover:bg-blue-100'

  return (
    <button
      onClick={handleClick}
      disabled={isPending}
      title={label}
      className={`inline-flex items-center gap-1 text-xs rounded px-1.5 py-0.5 transition-colors ml-1 ${chipClass} disabled:opacity-50`}
    >
      {isPending
        ? <Bot className="h-3 w-3 animate-spin" />
        : aswAiEnabled === false
          ? <BotOff className="h-3 w-3" />
          : <Bot className="h-3 w-3" />}
      {isPending
        ? 'KI läuft…'
        : aswAiEnabled === false
          ? 'KI aus'
          : 'KI generieren'}
    </button>
  )
}

function CustomerGroup({
  customer,
  mandantId,
  mandantName: _mandantName,
  filterMode,
  onTransfer,
  isPending,
  isOpen,
  onToggle,
  onPreview: _onPreview,
  customerOpts,
  moduleSapDraft,
  onUpdateOpts,
  isSelected,
  onToggleSelect,
  isPreviewActive,
  transferState,
}: {
  customer: CustomerNode
  mandantId: string
  mandantName: string
  filterMode: FilterMode
  onTransfer: (scope: TransferScope) => void
  isPending: boolean
  isOpen: boolean
  onToggle: () => void
  onPreview: (info: PreviewCustomerInfo) => void
  customerOpts: CustomerOptionEntry | undefined
  moduleSapDraft: boolean
  onUpdateOpts: (patch: Partial<Pick<CustomerOptionEntry, 'sapDraft' | 'testSend'>>) => void
  isSelected: boolean
  onToggleSelect: () => void
  isPreviewActive: boolean
  transferState?: { mode: SapDocumentMode; success: boolean }
}) {
  const { excludedSupportIds } = useSupportExclusion()

  const totalSupports = customer.invoicePositions.reduce((acc, p) => acc + p.supports.length, 0)
  // Customer total: dynamic based on selected services (AC12/AC14)
  // Only cleared, billable positions; service contract items (price=0) do not increase the total
  const customerTotal = customer.invoicePositions.reduce((sum, p) => {
    if (p.clearanceStatusGroup !== 'cleared') return sum
    if (p.billingEligibility?.canBeBilled === false) return sum
    const posTotal = p.supports.reduce((s2, s) => {
      if (excludedSupportIds.has(s.id)) return s2
      return s2 + (s.calculatedPrice ?? 0)
    }, 0)
    return sum + posTotal
  }, 0)
  // Display positions filtered by active filterMode
  const displayPositions = filterMode === 'all'
    ? customer.invoicePositions
    : customer.invoicePositions.filter((p) =>
        filterMode === 'cleared' ? p.clearanceStatusGroup === 'cleared' : p.clearanceStatusGroup === 'other',
      )

  const effectiveSapDraft = customerOpts?.sapDraft !== null && customerOpts?.sapDraft !== undefined
    ? customerOpts.sapDraft
    : moduleSapDraft
  const effectiveTestSend = customerOpts?.testSend ?? true

  // Transfer state for UI feedback: invoice=disabled button, draft=amber chip
  const isInvoiceTransferred = transferState?.mode === 'invoice' && transferState.success === true
  const isDraftTransferred = transferState?.mode === 'draft' && transferState.success === true

  return (
    <div className={`ml-4 border-l pl-4 mt-2 rounded-r-sm transition-colors ${effectiveTestSend ? 'border-amber-400' : 'border-border'} ${isPreviewActive ? 'bg-brand/5' : ''}`}>
      {/* Zeile 1: Checkbox + Name + Anzahl + Preis */}
      <div className="flex items-center gap-1 pt-1">
        <input
          type="checkbox"
          checked={isSelected}
          onChange={onToggleSelect}
          onClick={(e) => e.stopPropagation()}
          className="h-3.5 w-3.5 rounded border-border accent-brand shrink-0 cursor-pointer"
          title="Kunden für Sammelübertragung auswählen"
        />
        <button
          onClick={onToggle}
          className="flex items-center gap-1.5 text-sm font-semibold text-foreground hover:text-brand transition-colors flex-1 text-left min-w-0"
        >
          {isOpen ? <ChevronDown className="h-4 w-4 shrink-0" /> : <ChevronRight className="h-4 w-4 shrink-0" />}
          <span className={`truncate ${effectiveTestSend ? 'text-amber-700' : ''}`}>{customer.customerName}</span>
          {effectiveTestSend && (
            <span className="ml-1 shrink-0 inline-flex items-center gap-0.5 text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded px-1 py-0.5 font-normal">
              <FlaskConical className="h-2.5 w-2.5" />
              Probe
            </span>
          )}
          <span className="text-xs text-muted-foreground font-normal ml-1 shrink-0">({totalSupports})</span>
          <span className="ml-2 text-xs font-mono tabular-nums shrink-0 text-foreground font-semibold">
            {fmtEur(customerTotal)}
          </span>
        </button>
      </div>
      {/* Zeile 2: Aktions-Buttons */}
      <div className="flex items-center gap-1 pb-1 pl-6 flex-wrap">
        {/* Toggle 1: SAP-Draft vs. Invoice */}
        <button
          onClick={() => onUpdateOpts({ sapDraft: !effectiveSapDraft })}
          title={effectiveSapDraft ? 'SAP-Entwurf — klicken für direkte Erstellung' : 'Direkte Erstellung — klicken für Entwurf'}
          className={`inline-flex items-center gap-1 text-xs rounded px-1.5 py-0.5 transition-colors border shrink-0 ${
            effectiveSapDraft
              ? 'text-blue-700 bg-blue-50 border-blue-200 hover:bg-blue-100'
              : 'text-muted-foreground bg-muted border-border hover:bg-accent'
          }`}
        >
          <FilePen className="h-3 w-3" />
          {effectiveSapDraft ? 'Entwurf' : 'Rechnung'}
        </button>

        {/* Toggle 2: AI */}
        <AiCustomerToggle customer={customer} />

        {/* Toggle 3: TestSend vs. Production */}
        <button
          onClick={() => onUpdateOpts({ testSend: !effectiveTestSend })}
          title={effectiveTestSend ? 'Probeversand — klicken für produktiven Versand' : 'Produktiv — klicken für Probeversand'}
          className={`inline-flex items-center gap-1 text-xs rounded px-1.5 py-0.5 transition-colors border shrink-0 ${
            effectiveTestSend
              ? 'text-amber-700 bg-amber-50 border-amber-200 hover:bg-amber-100'
              : 'text-green-700 bg-green-50 border-green-200 hover:bg-green-100'
          }`}
        >
          <FlaskConical className="h-3 w-3" />
          {effectiveTestSend ? 'Probe' : 'Produktiv'}
        </button>

        {isInvoiceTransferred ? (
          <span className="inline-flex items-center gap-1 text-xs rounded px-1.5 py-0.5 border border-green-300 text-green-700 bg-green-50 shrink-0" title="Rechnung wurde in dieser Sitzung erfolgreich übertragen">
            <CheckCircle2 className="h-3 w-3" />
            Übertragen
          </span>
        ) : isDraftTransferred ? (
          <>
            <span className="inline-flex items-center gap-1 text-xs rounded px-1.5 py-0.5 border border-amber-300 text-amber-700 bg-amber-50 shrink-0" title="Entwurf wurde in dieser Sitzung erstellt — kann erneut übertragen werden">
              <FilePen className="h-3 w-3" />
              Entwurf erstellt
            </span>
            <Button
              variant="ghost"
              size="xs"
              onClick={() => onTransfer({
                clearanceStatusGroup: 'cleared',
                mandantId,
                customerId: customer.customerId,
                trigger: 'single_customer',
              })}
              disabled={isPending}
              className="shrink-0"
              title="Erneut übertragen (Entwurf kann mehrfach erstellt werden)"
            >
              <Send className="h-3 w-3 mr-1" />
              Erneut
            </Button>
          </>
        ) : (
          <Button
            variant="ghost"
            size="xs"
            onClick={() => onTransfer({
              clearanceStatusGroup: 'cleared',
              mandantId,
              customerId: customer.customerId,
              trigger: 'single_customer',
            })}
            disabled={isPending}
            className="shrink-0"
            title="Freigegebene Leistungen dieses Kunden übertragen"
          >
            <Send className="h-3 w-3 mr-1" />
            Übertragen
          </Button>
        )}
      </div>
      {isOpen && displayPositions.map((p) => (
        <InvoicePositionGroup key={p.id} position={p} />
      ))}
    </div>
  )
}

function MandantGroup({
  mandant,
  filterMode,
  onTransfer,
  isPending,
  collapsedCustomers,
  onCustomerToggle,
  searchTerm,
  onPreview,
  customerOptionsMap,
  moduleSettingsMap,
  onUpdateCustomerOpts,
  selectedCustomers,
  onToggleCustomerSelect,
  previewCustomerId,
  transferredCustomers,
}: {
  mandant: MandantNode
  filterMode: FilterMode
  onTransfer: (scope: TransferScope) => void
  isPending: boolean
  collapsedCustomers: Set<string>
  onCustomerToggle: (customerId: string) => void
  searchTerm: string
  onPreview: (info: PreviewCustomerInfo) => void
  customerOptionsMap: Map<string, CustomerOptionEntry>
  moduleSettingsMap: Map<string, AswTenantSetting>
  onUpdateCustomerOpts: (tenantId: string, customerId: string, patch: Partial<Pick<CustomerOptionEntry, 'sapDraft' | 'testSend'>>) => void
  selectedCustomers: Set<string>
  onToggleCustomerSelect: (customerId: string) => void
  previewCustomerId: string | null
  transferredCustomers: Map<string, { mode: SapDocumentMode; success: boolean }>
}) {
  const [open, setOpen] = useState(true)

  const term = searchTerm.trim().toLowerCase()
  const nameFiltered = term
    ? mandant.customers.filter(
        (c) =>
          c.customerName.toLowerCase().includes(term) ||
          String(c.customerId).toLowerCase().includes(term),
      )
    : mandant.customers
  // Filter by clearanceStatus group when a specific filter is active
  const filteredCustomers = filterMode === 'all'
    ? nameFiltered
    : nameFiltered.filter((c) =>
        c.invoicePositions.some((p) =>
          filterMode === 'cleared' ? p.clearanceStatusGroup === 'cleared' : p.clearanceStatusGroup === 'other',
        ),
      )

  if (filteredCustomers.length === 0 && (term || filterMode !== 'all')) return null

  const totalSupports = filteredCustomers.reduce(
    (acc, c) => acc + c.invoicePositions.reduce((pa, p) => pa + p.supports.length, 0),
    0,
  )

  const tenantSetting = moduleSettingsMap.get(mandant.mandantId)
  const moduleSapDraft = tenantSetting?.sapDocumentMode !== 'invoice'

  return (
    <Card className="mb-3">
      <Card.Header>
        <button
          onClick={() => setOpen((v: boolean) => !v)}
          className="flex items-center gap-2 w-full text-left font-semibold text-foreground hover:text-brand transition-colors"
        >
          {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          {mandant.mandantName}
          <span className="text-sm text-muted-foreground font-normal ml-1">
            · {filteredCustomers.length} Kunden · {totalSupports} Supports
          </span>
        </button>
      </Card.Header>
      {open && (
        <Card.Body className="pt-0">
          {filteredCustomers.map((c) => (
            <CustomerGroup
              key={c.customerId}
              customer={c}
              mandantId={mandant.mandantId}
              mandantName={mandant.mandantName}
              filterMode={filterMode}
              onTransfer={onTransfer}
              isPending={isPending}
              isOpen={!collapsedCustomers.has(c.customerId)}
              onToggle={() => {
                onCustomerToggle(c.customerId)
                onPreview({
                  mandantId: mandant.mandantId,
                  mandantName: mandant.mandantName,
                  customerId: c.customerId,
                  customerName: c.customerName,
                })
              }}
              onPreview={onPreview}
              customerOpts={customerOptionsMap.get(`${mandant.mandantId}:${c.customerId}`)}
              moduleSapDraft={moduleSapDraft}
              onUpdateOpts={(patch) => onUpdateCustomerOpts(mandant.mandantId, c.customerId, patch)}
              isSelected={selectedCustomers.has(c.customerId)}
              onToggleSelect={() => onToggleCustomerSelect(c.customerId)}
              isPreviewActive={previewCustomerId === c.customerId}
              transferState={transferredCustomers.get(`${mandant.mandantId}:${c.customerId}`)}
            />
          ))}
        </Card.Body>
      )}
    </Card>
  )
}

function SupportTree({
  mandants,
  filterMode,
  onTransfer,
  isPending,
  onPreview,
  customerOptionsMap,
  moduleSettingsMap,
  onUpdateCustomerOpts,
  onBulkUpdateCustomerOpts,
  bulkUpdatePending,
  previewCustomerId,
  transferredCustomers,
}: {
  mandants: MandantNode[]
  filterMode: FilterMode
  onTransfer: (scope: TransferScope) => void
  isPending: boolean
  onPreview: (info: PreviewCustomerInfo) => void
  customerOptionsMap: Map<string, CustomerOptionEntry>
  moduleSettingsMap: Map<string, AswTenantSetting>
  onUpdateCustomerOpts: (tenantId: string, customerId: string, patch: Partial<Pick<CustomerOptionEntry, 'sapDraft' | 'testSend'>>) => void
  onBulkUpdateCustomerOpts: (customers: Array<{ tenantId: string; customerId: string }>, patch: Partial<Pick<CustomerOptionEntry, 'sapDraft' | 'testSend'>>) => void
  bulkUpdatePending: boolean
  previewCustomerId: string | null
  transferredCustomers: Map<string, { mode: SapDocumentMode; success: boolean }>
}) {
  const [searchTerm, setSearchTerm] = useState('')
  const [collapsedCustomers, setCollapsedCustomers] = useState<Set<string>>(new Set())
  const [selectedCustomers, setSelectedCustomers] = useState<Set<string>>(new Set())
  const [excludedSupportIds, setExcludedSupportIds] = useState<Set<string | number>>(new Set())

  const onToggleSupportExclusion = useCallback((id: string | number) => {
    setExcludedSupportIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }, [])

  const exclusionCtxValue = useMemo(
    () => ({ excludedSupportIds, onToggleSupportExclusion }),
    [excludedSupportIds, onToggleSupportExclusion],
  )

  const allCustomerIds = mandants.flatMap((m) => m.customers.map((c) => c.customerId))

  const expandAll = () => setCollapsedCustomers(new Set())
  const collapseAll = () => setCollapsedCustomers(new Set(allCustomerIds))

  const toggleCustomer = (customerId: string) => {
    setCollapsedCustomers((prev: Set<string>) => {
      const next = new Set(prev)
      if (next.has(customerId)) {
        next.delete(customerId)
      } else {
        next.add(customerId)
      }
      return next
    })
  }

  const toggleCustomerSelect = (customerId: string) => {
    setSelectedCustomers((prev) => {
      const next = new Set(prev)
      if (next.has(customerId)) {
        next.delete(customerId)
      } else {
        next.add(customerId)
      }
      return next
    })
  }

  const term = searchTerm.trim().toLowerCase()
  const visibleCustomers = mandants.flatMap((m) =>
    (term
      ? m.customers.filter(
          (c) =>
            c.customerName.toLowerCase().includes(term) ||
            String(c.customerId).toLowerCase().includes(term),
        )
      : m.customers
    ).map((c) => ({ tenantId: m.mandantId, customerId: c.customerId }))
  )

  if (mandants.length === 0) {
    return <EmptyState title="Keine Supports" description="Für diesen Zeitraum wurden keine passenden Supports gefunden." />
  }

  return (
    <SupportExclusionContext.Provider value={exclusionCtxValue}>
    <div>
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <Input
          placeholder="Kunden suchen (Name oder Nummer)…"
          value={searchTerm}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
          leadingIcon={<Search className="h-4 w-4" />}
          className="max-w-xs"
        />
        <Button variant="ghost" size="sm" onClick={expandAll} title="Alle Kunden aufklappen">
          <ChevronsDown className="h-4 w-4 mr-1" />
          Alle auf
        </Button>
        <Button variant="ghost" size="sm" onClick={collapseAll} title="Alle Kunden zuklappen">
          <ChevronsUp className="h-4 w-4 mr-1" />
          Alle zu
        </Button>
        {selectedCustomers.size > 0 && (
          <Button
            variant="primary"
            size="sm"
            onClick={() => {
              onTransfer({
                clearanceStatusGroup: 'cleared',
                customerIds: [...selectedCustomers],
                trigger: 'multi_customer',
              })
              setSelectedCustomers(new Set())
            }}
            disabled={isPending}
          >
            <Send className="h-4 w-4 mr-1" />
            {selectedCustomers.size} Kunden übertragen
          </Button>
        )}
      </div>

      {/* Sammelaktionen */}
      <div className="flex items-center gap-2 mb-4 flex-wrap text-xs text-muted-foreground">
        <span className="shrink-0">Alle sichtbaren:</span>
        <button
          onClick={() => onBulkUpdateCustomerOpts(visibleCustomers, { sapDraft: true })}
          disabled={bulkUpdatePending}
          className="inline-flex items-center gap-1 rounded px-2 py-0.5 border border-blue-200 text-blue-700 bg-blue-50 hover:bg-blue-100 disabled:opacity-50 transition-colors"
        >
          <FilePen className="h-3 w-3" />
          → Entwurf
        </button>
        <button
          onClick={() => onBulkUpdateCustomerOpts(visibleCustomers, { sapDraft: false })}
          disabled={bulkUpdatePending}
          className="inline-flex items-center gap-1 rounded px-2 py-0.5 border border-border text-muted-foreground bg-muted hover:bg-accent disabled:opacity-50 transition-colors"
        >
          <FilePen className="h-3 w-3" />
          → Rechnung
        </button>
        <button
          onClick={() => onBulkUpdateCustomerOpts(visibleCustomers, { testSend: true })}
          disabled={bulkUpdatePending}
          className="inline-flex items-center gap-1 rounded px-2 py-0.5 border border-amber-200 text-amber-700 bg-amber-50 hover:bg-amber-100 disabled:opacity-50 transition-colors"
        >
          <FlaskConical className="h-3 w-3" />
          → Probe
        </button>
        <button
          onClick={() => onBulkUpdateCustomerOpts(visibleCustomers, { testSend: false })}
          disabled={bulkUpdatePending}
          className="inline-flex items-center gap-1 rounded px-2 py-0.5 border border-green-200 text-green-700 bg-green-50 hover:bg-green-100 disabled:opacity-50 transition-colors"
        >
          <Send className="h-3 w-3" />
          → Produktiv
        </button>
      </div>

      {mandants.map((m) => (
        <MandantGroup
          key={m.mandantId}
          mandant={m}
          filterMode={filterMode}
          onTransfer={onTransfer}
          isPending={isPending}
          collapsedCustomers={collapsedCustomers}
          onCustomerToggle={toggleCustomer}
          searchTerm={searchTerm}
          onPreview={onPreview}
          customerOptionsMap={customerOptionsMap}
          moduleSettingsMap={moduleSettingsMap}
          onUpdateCustomerOpts={onUpdateCustomerOpts}
          selectedCustomers={selectedCustomers}
          onToggleCustomerSelect={toggleCustomerSelect}
          previewCustomerId={previewCustomerId}
          transferredCustomers={transferredCustomers}
        />
      ))}
    </div>
    </SupportExclusionContext.Provider>
  )
}

// ─── External Clearance Info Tab ─────────────────────────────────────────────

function ExternalClearanceTab() {
  return (
    <Card>
      <Card.Body className="flex flex-col gap-4">
        <div className="flex items-start gap-3">
          <Info className="w-5 h-5 text-brand shrink-0 mt-0.5" />
          <div className="space-y-2">
            <p className="text-sm font-medium text-foreground">
              Freigabe durch externes System
            </p>
            <p className="text-sm text-muted-foreground">
              Die Freigabe von Leistungen wird durch ein externes System gesteuert.
            </p>
            <p className="text-sm text-muted-foreground">
              Der Support-Workflow verarbeitet ausschließlich Leistungen, die vom externen System
              als <code className="text-xs bg-muted px-1 py-0.5 rounded font-mono">CLEARED</code> markiert
              wurden und noch nicht abgerechnet sind (<code className="text-xs bg-muted px-1 py-0.5 rounded font-mono">booked = false</code> und
              leere <code className="text-xs bg-muted px-1 py-0.5 rounded font-mono">erpNumber</code>).
            </p>
            <p className="text-sm text-muted-foreground">
              Interne Freigabelogik über Definitionen ist nicht mehr aktiv.
            </p>
          </div>
        </div>
      </Card.Body>
    </Card>
  )
}

// ─── Transfer Results ─────────────────────────────────────────────────────────

function TransferReportView({ report }: { report: TransferReport }) {
  return (
    <div className="mt-6 space-y-3">
      {report.devModeActive && (
        <div className="flex items-center gap-2 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          <span><strong>Dev-Modus aktiv</strong> — SAP-Calls wurden simuliert, nicht gesendet. Ergebnisse wurden im Dev-Log gespeichert.</span>
        </div>
      )}

      <div className="flex items-center gap-4 text-sm font-medium">
        <span className={report.devModeActive ? 'text-amber-700' : 'text-green-600'}>
          {report.totalSent} {report.devModeActive ? 'simuliert' : 'erfolgreich übertragen'}
        </span>
        {report.totalFailed > 0 && (
          <span className="text-destructive">{report.totalFailed} fehlgeschlagen</span>
        )}
        {report.skippedMandants.length > 0 && (
          <span className="text-muted-foreground">{report.skippedMandants.length} Mandanten übersprungen</span>
        )}
      </div>

      {report.results.map((r, i) => (
        <Card key={i}>
          <div className="p-4">
            <div className="min-w-0">
              <div className="flex items-center gap-2 text-sm font-medium">
                {r.success
                  ? <CheckCircle2 className={`h-4 w-4 shrink-0 ${r.devMode ? 'text-amber-500' : 'text-green-500'}`} />
                  : <AlertTriangle className="h-4 w-4 text-destructive shrink-0" />
                }
                <span>{r.customerName}</span>
                {r.cardCode && <span className="text-muted-foreground font-normal">· CardCode: {r.cardCode}</span>}
                {r.devMode && <span className="rounded bg-amber-100 px-1.5 py-0.5 text-xs font-medium text-amber-700">DEV</span>}
              </div>
              <div className="text-xs text-muted-foreground mt-1 ml-6">
                Mandant {r.mandantId} · {r.mode === 'draft' ? 'Entwurf' : 'Rechnung'} → {r.endpoint}
                {r.sapDocEntry !== undefined && ` · DocEntry: ${r.sapDocEntry}`}
                {r.sapDocNum !== undefined && ` · DocNum: ${r.sapDocNum}`}
              </div>
              {r.error && (
                <div className="text-xs text-destructive mt-1 ml-6">{r.error}</div>
              )}
              {r.skippedPositions && r.skippedPositions.length > 0 && (
                <div className="text-xs text-amber-600 mt-1 ml-6">
                  {r.skippedPositions.length} Position(en) übersprungen: {r.skippedPositions.map((s) => s.reason).join('; ')}
                </div>
              )}
              {r.emailResult && (
                <div className="flex items-center gap-1.5 text-xs mt-1 ml-6">
                  {r.emailResult.emailSent
                    ? <MailCheck className="h-3.5 w-3.5 text-green-600 shrink-0" />
                    : r.emailResult.emailAttempted === false
                      ? <Mail className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                      : <MailX className="h-3.5 w-3.5 text-destructive shrink-0" />
                  }
                  {r.emailResult.emailSent
                    ? <span className="text-green-700">E-Mail gesendet an {r.emailResult.actualRecipientEmail}{r.emailResult.redirectedBecause ? ' [DEV-Redirect]' : ''}{!r.emailResult.pdfCreated ? ' (ohne PDF)' : ''}</span>
                    : r.emailResult.emailAttempted === false
                      ? <span className="text-muted-foreground">E-Mail nicht versucht: {r.emailResult.reason ?? r.emailResult.error}</span>
                      : <span className="text-destructive">E-Mail fehlgeschlagen: {r.emailResult.error}</span>
                  }
                </div>
              )}
            </div>
          </div>
        </Card>
      ))}

      {report.skippedMandants.length > 0 && (
        <div className="text-xs text-muted-foreground mt-2">
          Übersprungene Mandanten: {report.skippedMandants.map((m) => `${m.mandantId} (${m.reason})`).join(', ')}
        </div>
      )}
    </div>
  )
}

// ─── Supports Tab ─────────────────────────────────────────────────────────────

function SupportsTab() {
  const { data, isLoading, error } = useSupportTree()
  const refreshMut = useRefreshSupportTree()
  const transferMut = useTransfer()
  const sendEmailMut = useSendEmail()
  const enrichAiMut = useEnrichAi()
  const { data: tenantSettings } = useAswTenantSettings()
  const { data: customerOptionsList } = useCustomerOptions()
  const updateCustomerOptMut = useUpdateCustomerOption()
  const bulkUpdateOptsMut = useBulkUpdateCustomerOptions()
  const qc = useQueryClient()
  const { toast } = useToast()
  const isFetching = refreshMut.isPending
  const [transferReport, setTransferReport] = useState<TransferReport | null>(null)
  const [confirmScope, setConfirmScope] = useState<TransferScope | null>(null)
  const [previewCustomer, setPreviewCustomer] = useState<PreviewCustomerInfo | null>(null)
  const [_isSendingEmail, setIsSendingEmail] = useState(false)
  const [filterMode, setFilterMode] = useState<FilterMode>('all')
  const [showFilterDiag, setShowFilterDiag] = useState(false)
  const [transferDialogOpen, setTransferDialogOpen] = useState(false)
  // Tracks post-transfer state per customer for UI feedback (session-only)
  const [transferredCustomers, setTransferredCustomers] = useState<Map<string, { mode: SapDocumentMode; success: boolean }>>(new Map())
  const hasAutoOpened = useRef(false)

  const allMandants = useMemo(
    () => mergeMandants(data?.cleared ?? [], data?.other ?? []),
    [data?.cleared, data?.other],
  )

  const handlePreview = useCallback((info: PreviewCustomerInfo) => {
    setPreviewCustomer(info)
  }, [])

  // Auto-open the first customer preview when data is first loaded
  useEffect(() => {
    if (hasAutoOpened.current || !data) return
    const merged = mergeMandants(data.cleared, data.other)
    const firstMandant = merged[0]
    if (!firstMandant) return
    const firstCustomer = firstMandant.customers[0]
    if (!firstCustomer) return
    hasAutoOpened.current = true
    handlePreview({
      mandantId: firstMandant.mandantId,
      mandantName: firstMandant.mandantName,
      customerId: firstCustomer.customerId,
      customerName: firstCustomer.customerName,
    })
  }, [data, handlePreview])

  const isAiConfigured = (tenantSettings ?? []).some((s) => s.aiMode !== 'never')

  const customerOptionsMap = new Map<string, CustomerOptionEntry>(
    (customerOptionsList ?? []).map((o) => [`${o.tenantId}:${o.customerId}`, o]),
  )
  const moduleSettingsMap = new Map<string, AswTenantSetting>(
    (tenantSettings ?? []).map((s) => [s.tenantId, s]),
  )

  const handleUpdateCustomerOpts = (
    tenantId: string,
    customerId: string,
    patch: Partial<Pick<CustomerOptionEntry, 'sapDraft' | 'testSend'>>,
  ) => {
    updateCustomerOptMut.mutate({ tenantId, customerId, patch }, {
      onError: (err) => toast({ title: 'Einstellung konnte nicht gespeichert werden', description: (err as Error).message, tone: 'danger' }),
    })
  }

  const handleBulkUpdateCustomerOpts = (
    customers: Array<{ tenantId: string; customerId: string }>,
    patch: Partial<Pick<CustomerOptionEntry, 'sapDraft' | 'testSend'>>,
  ) => {
    bulkUpdateOptsMut.mutate({ customers, patch }, {
      onError: (err) => toast({ title: 'Sammeleinstellung fehlgeschlagen', description: (err as Error).message, tone: 'danger' }),
    })
  }

  const executeTransfer = async (scope: TransferScope) => {
    setTransferReport(null)
    setIsSendingEmail(false)
    setTransferDialogOpen(true)

    let report: TransferReport
    try {
      report = await transferMut.mutateAsync(scope)
    } catch (err) {
      toast({ title: 'Übertragung fehlgeschlagen', description: (err as Error).message, variant: 'destructive' })
      return
    }

    setTransferReport(report)
    // Update per-customer transfer state for UI feedback
    setTransferredCustomers((prev) => {
      const next = new Map(prev)
      for (const r of report.results) {
        const key = `${r.mandantId}:${r.customerId}`
        next.set(key, { mode: r.mode, success: r.success })
      }
      return next
    })
    if (report.totalFailed === 0) {
      toast({ title: `${report.totalSent} Beleg(e) erfolgreich übertragen`, variant: 'default' })
    } else {
      toast({ title: `${report.totalFailed} Fehler bei der Übertragung`, variant: 'destructive' })
    }

    // Step 2: Send email asynchronously for each successful transfer
    const emailPending = report.results.filter((r) => r.success && r.emailSkipped)
    if (emailPending.length > 0) {
      setIsSendingEmail(true)
      // Use merged tree to find customer positions (unified view)
      const mergedForEmail = mergeMandants(data?.cleared ?? [], data?.other ?? [])
      const updatedResults = [...report.results]

      for (const result of emailPending) {
        const mandant = mergedForEmail.find((m) => String(m.mandantId) === result.mandantId)
        // Only cleared positions go into the email (Aufgabe 2)
        const customer = mandant?.customers.find((c) => String(c.customerId) === result.customerId)
        // Only send cleared positions in the email (Aufgabe 2)
        const positions: SendEmailParams['positions'] = (customer?.invoicePositions ?? [])
          .filter((p) => p.clearanceStatusGroup === 'cleared')
          .map((p) => ({
          id: p.id,
          type: p.type,
          linkId: p.linkId,
          ticketTitle: p.ticketTitle,
          supportIds: p.supportIds,
          invoiceText: p.invoiceText ? {
            rawText: p.invoiceText.rawText,
            pdfText: p.invoiceText.pdfText,
            pdfHtml: p.invoiceText.pdfHtml,
          } : null,
          materialItem: p.materialItem ? {
            articleNumber: p.materialItem.articleNumber,
            label: p.materialItem.label,
            quantity: p.materialItem.quantity,
            price: p.materialItem.price,
          } : null,
          calculatedPrice: p.calculatedPrice,
        }))

        try {
          const emailResult = await sendEmailMut.mutateAsync({
            mandantId: result.mandantId,
            mandantName: result.mandantName,
            customerId: result.customerId,
            customerName: result.customerName,
            cardCode: result.cardCode,
            mode: result.mode,
            devMode: result.devMode,
            sapDocEntry: result.sapDocEntry,
            sapDocNum: result.sapDocNum,
            positions,
          })
          const idx = updatedResults.findIndex((r) => r.mandantId === result.mandantId && r.customerId === result.customerId)
          if (idx >= 0) {
            updatedResults[idx] = { ...updatedResults[idx]!, emailResult, emailSkipped: false }
          }
        } catch (emailErr) {
          const errorMsg = (emailErr as Error).message
          const idx = updatedResults.findIndex((r) => r.mandantId === result.mandantId && r.customerId === result.customerId)
          if (idx >= 0) {
            updatedResults[idx] = {
              ...updatedResults[idx]!,
              emailResult: {
                mandantId: result.mandantId,
                customerId: result.customerId,
                sapDocumentMode: result.mode,
                invoiceNumber: result.sapDocNum !== undefined ? String(result.sapDocNum) : result.sapDocEntry !== undefined ? String(result.sapDocEntry) : '',
                invoiceDocEntry: result.sapDocEntry ?? 0,
                pdfCreated: false,
                emailAttempted: true,
                emailSent: false,
                error: errorMsg,
              },
              emailSkipped: false,
            }
          }
        }
      }

      setTransferReport({ ...report, results: updatedResults })
      setIsSendingEmail(false)
    }
  }

  const handleTransfer = (scope: TransferScope) => {
    if (scope.clearanceStatusGroup === 'other') {
      setConfirmScope(scope)
    } else {
      executeTransfer(scope)
    }
  }

  const handleEnrichAi = () => {
    enrichAiMut.mutate(undefined, {
      onSuccess: (enrichedTree) => {
        qc.setQueryData(['automatic-support-workflow'], enrichedTree)
        toast({ title: 'KI-Texte wurden hinzugefügt', tone: 'success' })
      },
      onError: (err) => {
        toast({ title: 'KI-Anreicherung fehlgeschlagen', description: (err as Error).message, tone: 'danger' })
      },
    })
  }

  return (
    <div className="flex gap-4 items-start">
      {transferMut.isPending && transferDialogOpen && (
        <Modal open={true} onClose={() => setTransferDialogOpen(false)} title="">
          <div className="flex flex-col items-center gap-4 py-8 px-4">
            <Loader2 className="h-10 w-10 animate-spin text-brand" />
            <div className="text-center">
              <p className="font-medium text-foreground">Rechnung wird übertragen…</p>
              <p className="text-sm text-muted-foreground mt-1">Bitte warten, SAP-Beleg wird erstellt.</p>
            </div>
          </div>
        </Modal>
      )}
      <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2 mb-6 flex-wrap">
        <Button
          variant="secondary"
          size="sm"
          onClick={() => void refreshMut.mutate()}
          disabled={isFetching}
        >
          <RefreshCcw className={`h-4 w-4 mr-1.5 ${isFetching ? 'animate-spin' : ''}`} />
          Neu laden
        </Button>
        {data?.meta.fetchedAt && (
          <span className="text-xs text-muted-foreground">
            Stand: {new Intl.DateTimeFormat('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }).format(new Date(data.meta.fetchedAt))} Uhr
          </span>
        )}
        {isAiConfigured && (
          <Button
            variant="secondary"
            size="sm"
            onClick={handleEnrichAi}
            disabled={enrichAiMut.isPending || !data}
          >
            <Bot className={`h-4 w-4 mr-1.5 ${enrichAiMut.isPending ? 'animate-spin' : ''}`} />
            {enrichAiMut.isPending ? 'KI läuft…' : 'KI-Texte hinzufügen'}
          </Button>
        )}
        <Button
          variant="primary"
          size="sm"
          onClick={() => handleTransfer({ clearanceStatusGroup: 'cleared', trigger: 'send_all_cleared' })}
          disabled={transferMut.isPending || !data || data.meta.clearedCount === 0}
        >
          <Send className="h-4 w-4 mr-1.5" />
          {transferMut.isPending ? 'Wird übertragen…' : 'Freigegebene senden'}
        </Button>
      </div>

      {confirmScope && (
        <ConfirmDialog
          open={true}
          onClose={() => setConfirmScope(null)}
          onConfirm={() => {
            const scope = confirmScope
            setConfirmScope(null)
            executeTransfer(scope)
          }}
          title="Nicht freigegebene Leistungen senden?"
          description={
            confirmScope.customerId
              ? `Die Leistungen dieses Kunden werden übertragen, auch wenn sie nicht freigegeben sind.`
              : `Alle sonstigen (nicht freigegebenen) Leistungen werden übertragen.`
          }
          confirmLabel="Übertragen"
          variant="danger"
        />
      )}

      {isLoading && <LoadingState />}
      {error && (
        <ErrorState
          title="Supports konnten nicht geladen werden"
          description={(error as Error).message}
          onRetry={() => void refreshMut.mutate()}
        />
      )}

      {transferReport && <TransferReportView report={transferReport} />}

      {data && (
        <>
          {/* Filter-Bar: Alle / Freigegeben / Nicht freigegeben */}
          <div className="flex items-center gap-2 mb-4 flex-wrap">
            {(
              [
                { mode: 'all' as FilterMode, label: `Alle (${data.meta.clearedCount + data.meta.otherCount})`, icon: null },
                { mode: 'cleared' as FilterMode, label: `Freigegeben (${data.meta.clearedCount})`, icon: <CheckCircle2 className="h-3.5 w-3.5 text-green-500" /> },
                { mode: 'other' as FilterMode, label: `Nicht freigegeben / sonstige (${data.meta.otherCount})`, icon: <Clock className="h-3.5 w-3.5 text-amber-500" /> },
              ] as const
            ).map(({ mode, label, icon }) => (
              <button
                key={mode}
                onClick={() => setFilterMode(mode)}
                className={`inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-lg border transition-colors ${
                  filterMode === mode
                    ? 'border-brand bg-brand/10 text-brand font-medium'
                    : 'border-border bg-background text-muted-foreground hover:bg-muted'
                }`}
              >
                {icon}
                {label}
              </button>
            ))}
          </div>

          {/* Filter-Diagnose: zeigt warum Leistungen ausgeblendet sind */}
          <FilterDiagPanel stats={data.meta.filterStats} open={showFilterDiag} onToggle={() => setShowFilterDiag(v => !v)} />

          <SupportTree
            mandants={allMandants}
            filterMode={filterMode}
            onTransfer={handleTransfer}
            isPending={transferMut.isPending}
            onPreview={handlePreview}
            customerOptionsMap={customerOptionsMap}
            moduleSettingsMap={moduleSettingsMap}
            onUpdateCustomerOpts={handleUpdateCustomerOpts}
            onBulkUpdateCustomerOpts={handleBulkUpdateCustomerOpts}
            bulkUpdatePending={bulkUpdateOptsMut.isPending}
            previewCustomerId={previewCustomer?.customerId ?? null}
            transferredCustomers={transferredCustomers}
          />
        </>
      )}
      </div>
      {/* Permanent invoice preview panel — always visible, empty state when no customer selected */}
      <div className="w-1/2 shrink-0 sticky top-6" style={{ height: 'calc(100vh - 120px)' }}>
        <InvoicePreviewPanel customer={previewCustomer} />
      </div>
    </div>
  )
}

// ─── Settings Tab (Redirect) ──────────────────────────────────────────────────

function SettingsTab() {
  return (
    <Card>
      <Card.Body className="flex flex-col gap-4 items-start">
        <div className="flex items-start gap-3">
          <Settings className="w-5 h-5 text-muted-foreground shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-foreground">Einstellungen wurden verschoben</p>
            <p className="text-sm text-muted-foreground mt-1">
              Die Einstellungen für dieses Modul werden zentral unter{' '}
              <strong>Einstellungen → Module → Finanzen → Support-Workflow</strong> verwaltet.
            </p>
          </div>
        </div>
        <RouterLink
          to="/einstellungen/module/finanzen"
          className="flex items-center gap-1.5 px-3 py-1.5 bg-brand-600 hover:bg-brand-700 text-white text-sm font-medium rounded-lg transition-colors"
        >
          Zu den Moduleinstellungen →
        </RouterLink>
      </Card.Body>
    </Card>
  )
}

// ─── Templates Tab ────────────────────────────────────────────────────────────

interface TemplateFormData {
  name: string
  subjectTemplate: string
  bodyTemplate: string
  active: boolean
}

const DEFAULT_SUBJECT = 'Ihre Rechnung %%invoiceNumber%% von %%mandantName%%'
const DEFAULT_BODY = `Sehr geehrte Damen und Herren,

anbei erhalten Sie unsere Rechnung %%invoiceNumber%% vom %%invoiceDate%% als PDF.

Mandant: %%mandantName%%
Kunde: %%customerName%%

Bei Rückfragen stehen wir Ihnen gerne zur Verfügung.

Mit freundlichen Grüßen
%%mandantName%%`

const TEMPLATE_VARIABLES = [
  '%%mandantName%%', '%%customerName%%', '%%customerNumber%%', '%%cardCode%%',
  '%%invoiceNumber%%', '%%invoiceDocEntry%%', '%%invoiceDate%%', '%%invoiceDueDate%%',
  '%%invoiceTotal%%', '%%currency%%', '%%sapDocumentMode%%', '%%sapDocumentType%%', '%%contactEmail%%',
]

function TemplateFormModal({
  open,
  onClose,
  initial,
  onSave,
  saving,
}: {
  open: boolean
  onClose: () => void
  initial?: AswEmailTemplate
  onSave: (data: TemplateFormData) => void
  saving: boolean
}) {
  const [form, setForm] = useState<TemplateFormData>(() => ({
    name: initial?.name ?? '',
    subjectTemplate: initial?.subjectTemplate ?? DEFAULT_SUBJECT,
    bodyTemplate: initial?.bodyTemplate ?? DEFAULT_BODY,
    active: initial?.active ?? true,
  }))

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={initial ? 'Template bearbeiten' : 'Neues E-Mail-Template'}
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose} disabled={saving}>Abbrechen</Button>
          <Button variant="primary" onClick={() => onSave(form)} disabled={saving || !form.name.trim()}>
            {saving ? 'Speichern…' : 'Speichern'}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-foreground mb-1">Name</label>
          <Input
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            placeholder="z.B. Standard-Rechnungsmail"
          />
        </div>

        <div className="flex items-center justify-between py-1">
          <div>
            <div className="text-sm font-medium text-foreground">Aktiv</div>
          </div>
          <Switch
            checked={form.active}
            onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground mb-1">Betreff</label>
          <Input
            value={form.subjectTemplate}
            onChange={(e) => setForm((f) => ({ ...f, subjectTemplate: e.target.value }))}
            placeholder="Ihre Rechnung %%invoiceNumber%% von %%mandantName%%"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground mb-1">E-Mail-Text</label>
          <Textarea
            value={form.bodyTemplate}
            onChange={(e) => setForm((f) => ({ ...f, bodyTemplate: e.target.value }))}
            rows={10}
            placeholder="E-Mail-Text mit %%Variablen%%"
            className="font-mono text-xs"
          />
        </div>

        <div className="rounded border border-border bg-muted/50 p-3">
          <div className="text-xs font-medium text-muted-foreground mb-2">Verfügbare Variablen</div>
          <div className="flex flex-wrap gap-1.5">
            {TEMPLATE_VARIABLES.map((v) => (
              <code key={v} className="text-xs bg-background border border-border rounded px-1.5 py-0.5 text-muted-foreground">{v}</code>
            ))}
          </div>
        </div>
      </div>
    </Modal>
  )
}

function TemplatesTab() {
  const { toast } = useToast()
  const { data: templates = [], isLoading, error } = useAswEmailTemplates()
  const [editTemplate, setEditTemplate] = useState<AswEmailTemplate | null | 'new'>(null)
  const [deleteTemplate, setDeleteTemplate] = useState<AswEmailTemplate | null>(null)

  const createMut = useCreateAswEmailTemplate()
  const updateMut = useUpdateAswEmailTemplate()
  const deleteMut = useDeleteAswEmailTemplate()

  if (isLoading) return <LoadingState />
  if (error) return <ErrorState title="Templates konnten nicht geladen werden" description={(error as Error).message} />

  return (
    <div>
      <div className="flex items-start justify-between mb-4">
        <p className="text-sm text-muted-foreground max-w-xl">
          E-Mail-Templates für den Rechnungsversand. Variablen wie <code className="text-xs bg-muted px-1 py-0.5 rounded">%%invoiceNumber%%</code> werden beim Versand ersetzt.
        </p>
        <Button variant="primary" size="sm" onClick={() => setEditTemplate('new')}>
          <Plus className="h-4 w-4 mr-1.5" />
          Neues Template
        </Button>
      </div>

      {templates.length === 0 && (
        <EmptyState
          title="Keine Templates"
          description="Erstelle ein E-Mail-Template für den Rechnungsversand."
          action={<Button variant="primary" onClick={() => setEditTemplate('new')}>Erstes Template erstellen</Button>}
        />
      )}

      <div className="space-y-3">
        {templates.map((t) => (
          <Card key={t.id}>
            <Card.Body>
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <Mail className="h-4 w-4 text-muted-foreground shrink-0" />
                    <span className="font-semibold text-foreground">{t.name}</span>
                    <span className={`text-xs px-1.5 py-0.5 rounded-full ${t.active ? 'bg-green-100 text-green-700' : 'bg-muted text-muted-foreground'}`}>
                      {t.active ? 'Aktiv' : 'Inaktiv'}
                    </span>
                  </div>
                  <div className="mt-1 text-sm text-muted-foreground ml-6 truncate">{t.subjectTemplate}</div>
                  <div className="mt-0.5 text-xs text-muted-foreground ml-6 font-mono">ID: {t.id}</div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button
                    onClick={() => setEditTemplate(t)}
                    className="p-1.5 rounded hover:bg-muted transition-colors text-muted-foreground"
                    title="Bearbeiten"
                  >
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => setDeleteTemplate(t)}
                    className="p-1.5 rounded hover:bg-muted transition-colors text-muted-foreground hover:text-destructive"
                    title="Löschen"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </Card.Body>
          </Card>
        ))}
      </div>

      {editTemplate !== null && (
        <TemplateFormModal
          open={true}
          onClose={() => setEditTemplate(null)}
          initial={editTemplate === 'new' ? undefined : editTemplate}
          saving={createMut.isPending || updateMut.isPending}
          onSave={(form) => {
            if (editTemplate === 'new') {
              createMut.mutate(form, {
                onSuccess: () => { toast({ title: 'Template erstellt', tone: 'success' }); setEditTemplate(null) },
                onError: () => toast({ title: 'Fehler beim Erstellen', tone: 'danger' }),
              })
            } else {
              updateMut.mutate({ id: editTemplate.id, patch: form }, {
                onSuccess: () => { toast({ title: 'Template gespeichert', tone: 'success' }); setEditTemplate(null) },
                onError: () => toast({ title: 'Fehler beim Speichern', tone: 'danger' }),
              })
            }
          }}
        />
      )}

      {deleteTemplate && (
        <ConfirmDialog
          open={true}
          onClose={() => setDeleteTemplate(null)}
          onConfirm={() => {
            deleteMut.mutate(deleteTemplate.id, {
              onSuccess: () => { toast({ title: 'Template gelöscht', tone: 'success' }); setDeleteTemplate(null) },
              onError: () => toast({ title: 'Fehler beim Löschen', tone: 'danger' }),
            })
          }}
          title="Template löschen"
          description={`"${deleteTemplate.name}" wird unwiderruflich gelöscht.`}
          confirmLabel="Löschen"
          variant="danger"
        />
      )}
    </div>
  )
}

// ─── Logs Tab ─────────────────────────────────────────────────────────────────

function LogsTab() {
  const { data, isLoading, error, refetch, isFetching } = useAswLogs()
  const [expandedId, setExpandedId] = useState<string | null>(null)

  if (isLoading) return <LoadingState />
  if (error) return <ErrorState title="Logs konnten nicht geladen werden" description={(error as Error).message} onRetry={() => void refetch()} />

  return (
    <div>
      <div className="flex items-center gap-2 mb-4">
        <Button variant="secondary" size="sm" onClick={() => void refetch()} disabled={isFetching}>
          <RefreshCcw className={`h-4 w-4 mr-1.5 ${isFetching ? 'animate-spin' : ''}`} />
          Aktualisieren
        </Button>
      </div>

      {!data || data.length === 0 ? (
        <EmptyState title="Noch keine Log-Einträge" description="Log-Einträge werden beim Transfer erzeugt." />
      ) : (
        <div className="space-y-2">
          {data.map((log) => {
            const isExpanded = expandedId === log.id
            const isDevLog = log.mode === 'dev'
            const statusColor = log.status === 'success'
              ? (isDevLog ? 'text-amber-600' : 'text-green-600')
              : log.status === 'error' ? 'text-destructive' : 'text-muted-foreground'

            return (
              <Card key={log.id}>
                <button
                  className="w-full text-left p-4"
                  onClick={() => setExpandedId(isExpanded ? null : log.id)}
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0 flex items-center gap-2 text-sm">
                      {isDevLog
                        ? <span className="rounded bg-amber-100 px-1.5 py-0.5 text-xs font-medium text-amber-700">DEV</span>
                        : <span className="rounded bg-green-100 px-1.5 py-0.5 text-xs font-medium text-green-700">LIVE</span>
                      }
                      <span className="font-medium">{log.customerName ?? log.customerId}</span>
                      <span className="text-muted-foreground">· Mandant {log.mandantId}</span>
                      <span className={`font-medium ${statusColor}`}>
                        · {log.status === 'success' ? (isDevLog ? 'Simuliert' : 'OK') : log.status === 'error' ? 'Fehler' : 'Übersprungen'}
                      </span>
                    </div>
                    <div className="shrink-0 flex items-center gap-3 text-xs text-muted-foreground">
                      {log.sapDocumentMode && <span>{log.sapDocumentMode === 'draft' ? 'Entwurf' : 'Rechnung'}</span>}
                      {log.invoiceDate && <span>{log.invoiceDate}</span>}
                      {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                    </div>
                  </div>
                  {log.summary.message && (
                    <div className="text-xs text-muted-foreground mt-1 ml-8">{log.summary.message}</div>
                  )}
                  {log.error && (
                    <div className="text-xs text-destructive mt-1 ml-8">{log.error}</div>
                  )}
                </button>

                {isExpanded && (
                  <div className="border-t border-border px-4 pb-4 pt-3 space-y-3">
                    {log.summary.supportIds && log.summary.supportIds.length > 0 && (
                      <div className="text-xs text-muted-foreground">
                        Support-IDs: {log.summary.supportIds.join(', ')}
                      </div>
                    )}

                    {log.sapDocEntry !== undefined && (
                      <div className="text-xs text-muted-foreground">
                        SAP DocEntry: {log.sapDocEntry}{log.sapDocNum !== undefined ? ` · DocNum: ${log.sapDocNum}` : ''}
                      </div>
                    )}

                    {isDevLog && log.devSapCalls && log.devSapCalls.length > 0 && (
                      <div>
                        <div className="text-xs font-medium text-amber-700 mb-1">Simulierte SAP-Calls</div>
                        {log.devSapCalls.map((call, ci) => (
                          <div key={ci} className="rounded border border-amber-200 bg-amber-50 p-3 text-xs font-mono">
                            <div className="font-semibold text-amber-800 mb-1">{call.method} {call.endpoint}</div>
                            <pre className="whitespace-pre-wrap text-amber-900 overflow-x-auto max-h-64">
                              {JSON.stringify(call.payload, null, 2)}
                            </pre>
                          </div>
                        ))}
                      </div>
                    )}

                    {isDevLog && log.plannedTanssActions && log.plannedTanssActions.length > 0 && (
                      <div>
                        <div className="text-xs font-medium text-amber-700 mb-1">Geplante TANSS-Aktionen (blockiert)</div>
                        {log.plannedTanssActions.map((action, ai) => (
                          <div key={ai} className="text-xs text-amber-800">
                            {action.type}{action.supportIds ? ` — IDs: ${action.supportIds.join(', ')}` : ''} · {action.skippedBecause}
                          </div>
                        ))}
                      </div>
                    )}

                    {log.summary.emailResult && (
                      <div className="flex items-start gap-2 rounded border border-border bg-muted p-2.5">
                        {log.summary.emailResult.emailSent
                          ? <MailCheck className="h-3.5 w-3.5 text-green-600 shrink-0 mt-0.5" />
                          : log.summary.emailResult.emailAttempted === false
                            ? <Mail className="h-3.5 w-3.5 text-muted-foreground shrink-0 mt-0.5" />
                            : <MailX className="h-3.5 w-3.5 text-destructive shrink-0 mt-0.5" />
                        }
                        <div className="text-xs space-y-0.5">
                          <div className="font-medium">
                            {log.summary.emailResult.emailSent
                              ? 'E-Mail versendet'
                              : log.summary.emailResult.emailAttempted === false
                                ? 'E-Mail nicht versucht'
                                : 'E-Mail-Versand fehlgeschlagen'}
                          </div>
                          {log.summary.emailResult.reason && !log.summary.emailResult.emailSent && (
                            <div className="text-muted-foreground font-mono text-[10px]">Grund: {log.summary.emailResult.reason}</div>
                          )}
                          {log.summary.emailResult.actualRecipientEmail && (
                            <div className="text-muted-foreground">
                              An: {log.summary.emailResult.actualRecipientEmail}
                              {log.summary.emailResult.redirectedBecause === 'dev_mode' && (
                                <span className="ml-1 text-amber-600">
                                  (DEV-Redirect{log.summary.emailResult.originalRecipientEmail ? ` von ${log.summary.emailResult.originalRecipientEmail}` : ' · keine Kunden-E-Mail'})
                                </span>
                              )}
                            </div>
                          )}
                          {log.summary.emailResult.customerEmailSource && (
                            <div className="text-muted-foreground font-mono text-[10px]">
                              E-Mail-Quelle: {log.summary.emailResult.customerEmailSource}
                              {log.summary.emailResult.contactName && ` · ${log.summary.emailResult.contactName}`}
                            </div>
                          )}
                          {log.summary.emailResult.templateName && (
                            <div className="text-muted-foreground">Template: {log.summary.emailResult.templateName}</div>
                          )}
                          {log.summary.emailResult.subject && (
                            <div className="text-muted-foreground">Betreff: {log.summary.emailResult.subject}</div>
                          )}
                          <div className="text-muted-foreground">
                            PDF: {log.summary.emailResult.pdfCreated ? 'erzeugt' : 'nicht verfügbar'}
                            {log.summary.emailResult.pdfSource && ` (${
                              log.summary.emailResult.pdfSource === 'workflow_invoice_document' ? 'aus Workflow-Daten' :
                              log.summary.emailResult.pdfSource === 'documents_module' ? 'SAP-Dokument' :
                              log.summary.emailResult.pdfSource === 'dev_generated_pdf' ? 'Dev-PDF' :
                              log.summary.emailResult.pdfSource
                            })`}
                            {' · '}Anhang: {log.summary.emailResult.attachmentAdded ? 'hinzugefügt' : 'nicht hinzugefügt'}
                            {log.summary.emailResult.attachmentName && ` · ${log.summary.emailResult.attachmentName}`}
                          </div>
                          {log.summary.emailResult.error && (
                            <div className="text-destructive">{log.summary.emailResult.error}</div>
                          )}
                          {log.summary.emailResult.pdfDebug && (
                            <details className="mt-1">
                              <summary className="cursor-pointer text-xs text-muted-foreground hover:text-foreground">PDF-Debug anzeigen</summary>
                              <div className="mt-1 rounded bg-muted p-2 text-xs font-mono space-y-0.5">
                                <div>pdfGenerationStarted: {String(log.summary.emailResult.pdfDebug.pdfGenerationStarted ?? false)}</div>
                                <div>pdfSourceAttempted: {log.summary.emailResult.pdfDebug.pdfSourceAttempted ?? '—'}</div>
                                <div>workflowInvoiceDocumentCreated: {String(log.summary.emailResult.pdfDebug.workflowInvoiceDocumentCreated ?? false)}</div>
                                {log.summary.emailResult.pdfDebug.workflowInvoiceDocumentPreview && (
                                  <div className="pl-2 border-l-2 border-muted-foreground/30">
                                    <div>↳ invoice: {log.summary.emailResult.pdfDebug.workflowInvoiceDocumentPreview.invoiceNumber}</div>
                                    <div>↳ positions: {log.summary.emailResult.pdfDebug.workflowInvoiceDocumentPreview.positionCount}</div>
                                    <div>↳ total: {log.summary.emailResult.pdfDebug.workflowInvoiceDocumentPreview.total} {log.summary.emailResult.pdfDebug.workflowInvoiceDocumentPreview.currency}</div>
                                  </div>
                                )}
                                <div>documentLinesCount: {log.summary.emailResult.pdfDebug.documentLinesCount ?? '—'}</div>
                                <div>pdfRendererCalled: {String(log.summary.emailResult.pdfDebug.pdfRendererCalled ?? false)} ({log.summary.emailResult.pdfDebug.pdfRendererName ?? '—'})</div>
                                <div>pdfCreated: {String(log.summary.emailResult.pdfDebug.pdfCreated ?? false)}</div>
                                <div>pdfBufferLength: {log.summary.emailResult.pdfDebug.pdfBufferLength ?? 0} bytes</div>
                                <div>htmlLength: {log.summary.emailResult.pdfDebug.htmlLength ?? 0} chars</div>
                                <div>attachmentCreated: {String(log.summary.emailResult.pdfDebug.attachmentCreated ?? false)}{log.summary.emailResult.pdfDebug.attachmentName ? ` · ${log.summary.emailResult.pdfDebug.attachmentName}` : ''}</div>
                                {log.summary.emailResult.pdfDebug.errorMessage && (
                                  <div className="text-destructive">
                                    <div>errorStep: {log.summary.emailResult.pdfDebug.errorStep}</div>
                                    <div>errorMessage: {log.summary.emailResult.pdfDebug.errorMessage}</div>
                                    {log.summary.emailResult.pdfDebug.errorStack && (
                                      <div className="opacity-70 break-all">{log.summary.emailResult.pdfDebug.errorStack}</div>
                                    )}
                                  </div>
                                )}
                              </div>
                            </details>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </Card>
            )
          })}
        </div>
      )}
    </div>
  )
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function AutomaticSupportWorkflowPage() {
  return (
    <div className="p-6 max-w-screen-2xl mx-auto">
      <PageHeader
        title="Support-Workflow"
        description="TANSS-Supports nach externem Freigabe-Status (clearanceStatus) gefiltert und nach Abrechnungsstand gruppiert"
        breadcrumbs={[{ label: 'Finanzen' }, { label: 'Support-Workflow' }]}
      />

      <Tabs defaultValue="supports">
        <TabsList>
          <TabsTrigger value="supports">Supports</TabsTrigger>
          <TabsTrigger value="clearance">Freigabe</TabsTrigger>
          <TabsTrigger value="settings">Einstellungen</TabsTrigger>
          <TabsTrigger value="templates">Templates</TabsTrigger>
          <TabsTrigger value="invoice-templates">Rechnungstemplates</TabsTrigger>
          <TabsTrigger value="logs">Logs</TabsTrigger>
        </TabsList>

        <TabsContent value="supports" className="mt-6">
          <SupportsTab />
        </TabsContent>

        <TabsContent value="clearance" className="mt-6">
          <ExternalClearanceTab />
        </TabsContent>

        <TabsContent value="settings" className="mt-6">
          <SettingsTab />
        </TabsContent>

        <TabsContent value="templates" className="mt-6">
          <TemplatesTab />
        </TabsContent>

        <TabsContent value="invoice-templates" className="mt-6">
          <InvoiceTemplatesTab />
        </TabsContent>

        <TabsContent value="logs" className="mt-6">
          <LogsTab />
        </TabsContent>
      </Tabs>
    </div>
  )
}
